import {
  CapabilityCoordinatorBase,
  CapabilityRegistry,
  CapabilityRegistryContainer,
} from '@trimble/web3d';
import {
  MapStationCapability,
  StationsNavigationCapability,
  findClosestStation,
} from '@trimble/web3d-capabilities';
import { Vector3 } from '@technology/web3d';
import { StationsCapabilityProvider } from '@trimble/web3d-providers';
import {
  detectArrayChanges,
  arePointsEqual,
  Point3D,
  partialPointToVector3,
} from '@trimble/utilities';
import { getLogger } from '@trimble/logging';
import {
  createEffect,
  createMemo,
  createRoot,
  getOwner,
  on,
  Owner,
  runWithOwner,
} from 'solid-js';
import { getActiveNavCap } from '../sync-coordinators/sync-source-helpers';
import { offerStationPickToActiveNavigation } from '../sync-coordinators/station-pick-helpers';

/**
 * Coordinates map station picks across panes.
 *
 * When the user clicks a station marker on a 2D map pane,
 * {@link MapStationCapability} sets its `pickedStationPosition` signal.
 * This coordinator watches that signal and moves other panes to that
 * location. If a pane already owns the viewer with another navigation
 * mode (path navigation), the pick is offered to that mode so it is not
 * torn down (NUMARK-1128). Otherwise the pane's
 * {@link StationsNavigationCapability} is activated. PaneSyncer's rAF
 * loop then propagates the camera change to remaining panes.
 */
export class MapStationSyncCoordinator extends CapabilityCoordinatorBase<CapabilityRegistry> {
  private disposeRoot: (() => void) | undefined;

  constructor(registry: CapabilityRegistry) {
    super(registry);

    this.disposeRoot = createRoot((dispose) => {
      const owner = getOwner();

      const mapStationCaps = createMemo(() =>
        registry.getPaneCapabilities(MapStationCapability)
      );

      createEffect(
        on(mapStationCaps, (capabilities, prevCapabilities) => {
          const { newItems } = detectArrayChanges(
            prevCapabilities,
            capabilities
          );
          for (const capability of newItems) {
            this.watchPickedStation(owner, capability);
          }
        })
      );

      return dispose;
    });
  }

  private watchPickedStation(
    owner: Owner | null,
    capability: MapStationCapability
  ): void {
    runWithOwner(owner, () => {
      createEffect(
        on(
          () => capability.pickedStationPosition(),
          (pos) => {
            if (!pos) return;
            this.syncPickToOtherPanes(capability, pos);
          }
        )
      );
    });
  }

  private syncPickToOtherPanes(
    sourceCapability: MapStationCapability,
    pickedPosition: Point3D
  ): void {
    const containers = this.registry.paneContainers();
    const sourceContainer = containers.find(
      (c) => c.get(MapStationCapability)() === sourceCapability
    );

    for (const container of containers) {
      if (container === sourceContainer) continue;

      const stationsNavCap = container.get(StationsNavigationCapability)();
      if (!stationsNavCap || stationsNavCap instanceof MapStationCapability) {
        continue;
      }

      this.navigateToClosestStation(container, stationsNavCap, pickedPosition);
    }
  }

  private navigateToClosestStation(
    container: CapabilityRegistryContainer,
    navCap: StationsNavigationCapability,
    position: Point3D
  ): void {
    const stationsProvider = container.get(StationsCapabilityProvider)();
    if (!stationsProvider) return;

    const stationPositions = stationsProvider
      .stationsInfo()
      .map((s) => s.position);

    const posVec = new Vector3(position.x, position.y, position.z);
    const closestStationInfo = findClosestStation(posVec, stationPositions);
    if (!closestStationInfo) return;

    const viewer = container.viewerWrapper()?.viewer;
    const alreadyAtStation =
      viewer &&
      arePointsEqual(viewer.camera.position, closestStationInfo.position);

    if (alreadyAtStation) return;

    if (
      offerStationPickToActiveNavigation(
        getActiveNavCap(container),
        navCap,
        partialPointToVector3(closestStationInfo.position)
      )
    ) {
      getLogger().debug(
        `[MapStationSync] offered pick to active navigation at station ${closestStationInfo.index.toString()}`
      );
      return;
    }

    navCap.goToStation(closestStationInfo.index);
    getLogger().debug(
      `[MapStationSync] navigated to station ${closestStationInfo.index.toString()}`
    );
  }

  public async cleanup(): Promise<void> {
    this.disposeRoot?.();
    this.disposeRoot = undefined;
  }
}
